import { makeStyles, mergeClasses, tokens } from '@fluentui/react-components';
import {
  Desktop20Regular,
  DeveloperBoard20Regular,
  Globe16Regular,
  Laptop20Regular,
  Router20Regular,
  Server20Regular,
  Shield12Regular,
  Tv20Regular,
} from '@fluentui/react-icons';
import { useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import type { Device, Relay } from '../../api/types';
import { PresenceDot } from '../../components/PresenceDot';
import { StatePill } from '../../components/StatePill';
import { formatMbit } from '../../lib/format';
import { vt } from '../../theme';
import { uptime } from '../overview/model';
import { deviceKind, type DeviceKind, type Lane, type LiveSession, type MapState } from './model';

/** The vertical rhythm of the tiers (mockup 02 `TIER`, measured from the internet bar). */
const NET_H = 34;
const RELAY_Y = 58;
const RELAY_H = 68;
const RELAY_W = 430;
const LANES_Y = 164;
/** Where the trunk drops turn toward their lane. */
const TRUNK_Y = LANES_Y - 24;
const LANE_GAP = 16;
const LANE_MIN = 220;
const HEAD = 38;
const ROW0 = 56;
const PITCH = 48;
const CARD_H = 40;
const FOOT = 32;
/** The spine's inset from the lane's left edge, and the stub to the card. */
const SPINE = 22;
const STUB = 14;
/** jsdom and the first paint measure nothing. */
const FALLBACK_W = 900;

/**
 * An orthogonal connector whose corners are cut at 60°, the mark's angle (mockup 02 `elbow`):
 * down from y1, across at atY, down to y2.
 */
export function elbow(x1: number, y1: number, x2: number, y2: number, atY: number): string {
  const r = (v: number) => Math.round(v * 10) / 10;
  const dir = x2 >= x1 ? 1 : -1;
  const c = Math.min(8, Math.abs(x2 - x1) / 2);
  if (c < 1) return `M ${r(x1)} ${r(y1)} L ${r(x1)} ${r(y2)}`;
  const ch = c / Math.sqrt(3);
  return (
    `M ${r(x1)} ${r(y1)} L ${r(x1)} ${r(atY - ch)} L ${r(x1 + dir * c)} ${r(atY)} ` +
    `L ${r(x2 - dir * c)} ${r(atY)} L ${r(x2)} ${r(atY + ch)} L ${r(x2)} ${r(y2)}`
  );
}

const ICONS: Record<DeviceKind, ReactNode> = {
  router: <Router20Regular />,
  storage: <Server20Regular />,
  laptop: <Laptop20Regular />,
  desktop: <Desktop20Regular />,
  tv: <Tv20Regular />,
  board: <DeveloperBoard20Regular />,
  device: <Desktop20Regular />,
};

const useStyles = makeStyles({
  root: { position: 'relative', flexShrink: 0 },
  svg: { position: 'absolute', inset: 0, overflow: 'visible', pointerEvents: 'none' },
  over: { zIndex: 1 },
  mono: { fontFamily: tokens.fontFamilyMonospace },
  internet: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    height: `${NET_H}px`,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    columnGap: '10px',
    paddingInline: '14px',
    borderRadius: '8px',
    border: `1px solid ${vt.surface2}`,
    color: vt.fg3,
  },
  internetLabel: { fontSize: '12px', fontWeight: tokens.fontWeightSemibold, color: vt.fg2 },
  internetAddr: { fontFamily: tokens.fontFamilyMonospace, fontSize: '11px', color: vt.fg3, flexGrow: 1, whiteSpace: 'nowrap' },
  internetRule: { fontFamily: tokens.fontFamilyMonospace, fontSize: '10px', color: vt.fg4, whiteSpace: 'nowrap' },
  relay: {
    position: 'absolute',
    top: `${RELAY_Y}px`,
    height: `${RELAY_H}px`,
    width: `${RELAY_W}px`,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    columnGap: '14px',
    paddingInline: '20px 16px',
    borderRadius: '8px',
    border: `1px solid ${vt.brand}`,
    backgroundColor: vt.brandTint,
  },
  relayMark: { width: '26px', height: '22.5px', flexShrink: 0, fill: vt.brand },
  relayBody: { display: 'flex', flexDirection: 'column', rowGap: '6px', flexGrow: 1, minWidth: 0 },
  relayTop: { display: 'flex', alignItems: 'center', columnGap: '10px' },
  relayHost: { fontSize: '13px', fontWeight: tokens.fontWeightSemibold, color: vt.fg1, whiteSpace: 'nowrap' },
  relaySub: { display: 'flex', alignItems: 'baseline', columnGap: '10px', whiteSpace: 'nowrap', overflow: 'hidden' },
  relayIp: { fontFamily: tokens.fontFamilyMonospace, fontSize: '11px', color: vt.fg2 },
  relayMeta: { fontSize: '10.5px', color: vt.fg3, overflow: 'hidden', textOverflow: 'ellipsis' },
  relayRate: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    rowGap: '8px',
    flexShrink: 0,
    fontFamily: tokens.fontFamilyMonospace,
    fontSize: '10px',
    color: vt.cyan,
  },
  liveDot: {
    width: '7.6px',
    height: '7.6px',
    borderRadius: '50%',
    backgroundColor: vt.cyan,
    boxShadow: `0 0 0 3px ${vt.tintCyan}`,
    flexShrink: 0,
  },
  quietDot: { backgroundColor: vt.fg4, boxShadow: 'none' },
  laneHead: {
    position: 'absolute',
    height: `${HEAD}px`,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    columnGap: '10px',
    paddingInline: '16px',
  },
  laneName: { fontSize: '13px', fontWeight: tokens.fontWeightSemibold, color: vt.fg1, whiteSpace: 'nowrap' },
  laneCidr: { fontFamily: tokens.fontFamilyMonospace, fontSize: '10px', color: vt.fg3, flexGrow: 1, whiteSpace: 'nowrap' },
  laneCount: { fontFamily: tokens.fontFamilyMonospace, fontSize: '10px', color: vt.fg4 },
  isolated: {
    height: '17px',
    lineHeight: '17px',
    paddingInline: '8px',
    borderRadius: tokens.borderRadiusCircular,
    fontSize: '10px',
    color: vt.orange,
    backgroundColor: vt.tintOrange,
  },
  laneEmpty: { position: 'absolute', fontSize: '11px', color: vt.fg4 },
  card: {
    position: 'absolute',
    height: `${CARD_H}px`,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    columnGap: '10px',
    padding: '0 10px 0 12px',
    borderRadius: '8px',
    border: `1px solid ${vt.surface2}`,
    backgroundColor: vt.pane,
    color: vt.fg2,
    fontFamily: 'inherit',
    textAlign: 'left',
    cursor: 'pointer',
    ':hover': { border: `1px solid ${vt.fg4}` },
    ':focus-visible': { outline: `2px solid ${vt.brand}`, outlineOffset: '1px' },
  },
  cardOff: {
    border: `1px dashed ${vt.fg4}`,
    opacity: 0.8,
    color: vt.fg4,
    ':hover': { border: `1px dashed ${vt.fg3}` },
  },
  cardSelected: {
    backgroundColor: vt.brandTint,
    border: `1px solid ${vt.brand}`,
    color: vt.brand,
    ':hover': { border: `1px solid ${vt.brand}` },
  },
  cardIcon: { display: 'flex', flexShrink: 0, '& svg': { width: '16px', height: '16px' } },
  cardText: { display: 'flex', flexDirection: 'column', rowGap: '1px', flexGrow: 1, minWidth: 0 },
  cardName: { fontSize: '12px', color: vt.fg1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  cardNameSelected: { fontWeight: tokens.fontWeightSemibold },
  cardIp: { fontFamily: tokens.fontFamilyMonospace, fontSize: '10px', color: vt.fg3 },
  dim: { color: vt.fg4 },
  cardSide: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end', rowGap: '6px', flexShrink: 0 },
  dot: { marginRight: '3px' },
  seen: { fontFamily: tokens.fontFamilyMonospace, fontSize: '10px', color: vt.fg4 },
  posture: {
    position: 'absolute',
    height: `${FOOT}px`,
    boxSizing: 'border-box',
    display: 'flex',
    alignItems: 'center',
    columnGap: '8px',
    paddingInline: '18px 12px',
    fontSize: '10.5px',
    color: vt.fg4,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
  postureIsolated: { color: vt.orange },
});

/** The VIPPER mark (vpn repo docs/mockups/lib/logo.mjs markPath). */
function Mark({ className }: { className: string }) {
  return (
    <svg className={className} viewBox="0 0 5 4.3301" aria-hidden>
      <path d="M 0 0 L 5 0 L 2.5 4.3301 L 1 1.7321 L 2 1.7321 L 2.5 2.5981 L 3.5 0.866 L 0.5 0.866 Z" />
    </svg>
  );
}

/** "relay.vipper.network" and "51820" from "relay.vipper.network:51820". */
function splitEndpoint(endpoint: string): { host: string; port: string } {
  const i = endpoint.lastIndexOf(':');
  return i > 0 ? { host: endpoint.slice(0, i), port: endpoint.slice(i + 1) } : { host: endpoint, port: '51820' };
}

export interface TopologyProps {
  relay: Relay | null;
  /** Current relay throughput; null = unknown. */
  relayMbit: number | null;
  peers: number;
  /** The active policy version (from the matrix); null = unknown. */
  policyVersion: number | null;
  now: number;
  lanes: Lane[];
  stateOf: (deviceId: string) => MapState;
  seenOf: (deviceId: string) => string;
  postureOf: (laneId: string) => string | null;
  /** Already filtered to what should be drawn. */
  sessions: LiveSession[];
  selectedId: string | null;
  onSelect: (device: Device) => void;
}

interface LaneBox {
  lane: Lane;
  x: number;
  spine: number;
  /** Row middles, by device id. */
  rows: Map<string, number>;
  lastMid: number | null;
}

/** Mockup 02's tiers: internet, the relay, one swim lane per network with its devices on a spine. */
export function Topology(p: TopologyProps) {
  const s = useStyles();
  const ref = useRef<HTMLDivElement>(null);
  const [measured, setMeasured] = useState(0);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const read = () => setMeasured(el.clientWidth);
    read();
    const ro = new ResizeObserver(read);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const n = Math.max(1, p.lanes.length);
  const minW = n * LANE_MIN + (n - 1) * LANE_GAP;
  const w = Math.max(measured || FALLBACK_W, minW);
  const laneW = (w - LANE_GAP * (n - 1)) / n;
  const hubX = w / 2;
  const relayBottom = RELAY_Y + RELAY_H;
  const maxRows = Math.max(1, ...p.lanes.map((l) => l.devices.length));
  const laneH = ROW0 + maxRows * PITCH + 16 + FOOT;
  const h = LANES_Y + laneH;

  const boxes: LaneBox[] = p.lanes.map((lane, i) => {
    const x = i * (laneW + LANE_GAP);
    const rows = new Map(lane.devices.map((d, j) => [d.id, LANES_Y + ROW0 + j * PITCH + CARD_H / 2]));
    return { lane, x, spine: x + SPINE, rows, lastMid: lane.devices.length ? LANES_Y + ROW0 + (lane.devices.length - 1) * PITCH + CARD_H / 2 : null };
  });

  // Each live session lights its two ends: the trunk into each lane, the spine down to the
  // card, and the stub. Two devices on one lane light the same trunk, which is the point.
  const lit = new Map<string, Set<string>>();
  for (const x of p.sessions) {
    for (const [lane, dev] of [
      [x.srcLane, x.srcId],
      [x.dstLane, x.dstId],
    ] as const) {
      if (!lit.has(lane)) lit.set(lane, new Set());
      lit.get(lane)!.add(dev);
    }
  }

  const { relay } = p;
  const ep = splitEndpoint(relay?.endpoint ?? 'relay');
  const running = relay?.state === 'running';
  const relayMeta = [
    running && relay ? uptime(relay, p.now) : null,
    `${p.peers} peer${p.peers === 1 ? '' : 's'}`,
    p.policyVersion !== null ? `policy v${p.policyVersion}` : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <div ref={ref} className={s.root} style={{ height: `${h}px`, minWidth: `${minW}px` }} data-testid="topology">
      <svg className={s.svg} width={w} height={h} aria-hidden>
        <line x1={hubX} y1={NET_H} x2={hubX} y2={RELAY_Y} stroke={vt.surface2} strokeWidth={1.2} />
        {boxes.map((b) => (
          <g key={b.lane.id}>
            <rect x={b.x + 0.5} y={LANES_Y + 0.5} width={laneW - 1} height={laneH - 1} rx={10} fill={vt.surface3} fillOpacity={0.4} stroke={vt.surface2} />
            <line x1={b.x + 1} x2={b.x + laneW - 1} y1={LANES_Y + HEAD} y2={LANES_Y + HEAD} stroke={vt.surface2} />
            <line x1={b.x + 1} x2={b.x + laneW - 1} y1={LANES_Y + laneH - FOOT} y2={LANES_Y + laneH - FOOT} stroke={vt.surface2} />
            <path d={elbow(hubX, relayBottom, b.spine, LANES_Y, TRUNK_Y)} fill="none" stroke={vt.surface2} strokeWidth={1.2} />
            {/* The spine stops at the last device: a line into empty space reads as a failed load. */}
            {b.lastMid !== null && <line x1={b.spine} x2={b.spine} y1={LANES_Y + HEAD} y2={b.lastMid} stroke={vt.surface2} strokeWidth={1.2} />}
            {b.lane.devices.map((d) => {
              const off = p.stateOf(d.id) === 'offline';
              const y = b.rows.get(d.id)!;
              return (
                <line
                  key={d.id}
                  x1={b.spine}
                  x2={b.spine + STUB}
                  y1={y}
                  y2={y}
                  stroke={off ? vt.fg4 : vt.surface2}
                  strokeWidth={1.2}
                  strokeDasharray={off ? '3 3' : undefined}
                />
              );
            })}
          </g>
        ))}
      </svg>

      <div className={s.internet} role="group" aria-label="Internet">
        <Globe16Regular />
        <span className={s.internetLabel}>Internet</span>
        <span className={s.internetAddr}>
          {relay?.publicIp ?? 'no public IP'} · {relay?.endpoint ?? '–'}
        </span>
        <span className={s.internetRule}>UDP {ep.port} in · all else denied</span>
      </div>

      <div className={s.relay} style={{ left: `${(w - RELAY_W) / 2}px` }} role="group" aria-label="Relay">
        <Mark className={s.relayMark} />
        <div className={s.relayBody}>
          <div className={s.relayTop}>
            <span className={s.relayHost}>{ep.host}</span>
            {relay && <StatePill state={relay.state} />}
          </div>
          <div className={s.relaySub}>
            <span className={s.relayIp}>{relay?.overlayIp ?? '–'}</span>
            <span className={s.relayMeta}>{relayMeta}</span>
          </div>
        </div>
        <div className={s.relayRate}>
          <span className={mergeClasses(s.liveDot, !p.sessions.length && s.quietDot)} aria-hidden />
          <span data-testid="relay-rate">{p.relayMbit === null ? '–' : formatMbit(p.relayMbit)}</span>
        </div>
      </div>

      {boxes.map((b) => {
        const { lane } = b;
        const posture = p.postureOf(lane.id);
        return (
          <section key={lane.id} aria-label={`Network ${lane.name}`} data-lane={lane.id}>
            <div className={s.laneHead} style={{ left: `${b.x}px`, top: `${LANES_Y}px`, width: `${laneW}px` }}>
              <span className={s.laneName}>{lane.name}</span>
              <span className={s.laneCidr}>{lane.cidr}</span>
              {lane.isolated ? (
                <span className={s.isolated}>isolated</span>
              ) : (
                <span className={s.laneCount} title={`${lane.devices.length} device${lane.devices.length === 1 ? '' : 's'}`}>
                  {lane.devices.length}
                </span>
              )}
            </div>
            {lane.devices.length === 0 && (
              <span className={s.laneEmpty} style={{ left: `${b.x + 16}px`, top: `${LANES_Y + ROW0 + 12}px` }}>
                No devices
              </span>
            )}
            {lane.devices.map((d) => {
              const state = p.stateOf(d.id);
              const off = state === 'offline';
              const sel = d.id === p.selectedId;
              const left = b.spine + STUB;
              return (
                <button
                  key={d.id}
                  type="button"
                  className={mergeClasses(s.card, off && s.cardOff, sel && s.cardSelected)}
                  style={{ left: `${left}px`, top: `${b.rows.get(d.id)! - CARD_H / 2}px`, width: `${b.x + laneW - 14 - left}px` }}
                  aria-pressed={sel}
                  aria-label={`${d.name} ${d.overlayIp} ${state}`}
                  data-device={d.id}
                  data-state={state}
                  onClick={() => p.onSelect(d)}
                >
                  <span className={s.cardIcon}>{ICONS[deviceKind(d)]}</span>
                  <span className={s.cardText}>
                    <span className={mergeClasses(s.cardName, sel && s.cardNameSelected, off && s.dim)}>{d.name}</span>
                    <span className={mergeClasses(s.cardIp, off && s.dim)}>{d.overlayIp}</span>
                  </span>
                  <span className={s.cardSide}>
                    <PresenceDot className={s.dot} state={state} decorative />

                    <span className={s.seen}>{p.seenOf(d.id)}</span>
                  </span>
                </button>
              );
            })}
            <div
              className={mergeClasses(s.posture, lane.isolated && s.postureIsolated)}
              style={{ left: `${b.x}px`, top: `${LANES_Y + laneH - FOOT}px`, width: `${laneW}px` }}
              data-testid={`posture-${lane.id}`}
            >
              <Shield12Regular />
              <span>{posture ?? (lane.id.startsWith(':') ? 'outside every network' : '–')}</span>
            </div>
          </section>
        );
      })}

      {/* Drawn last, over everything: the one moving thing on the page is the one nothing hides. */}
      <svg className={mergeClasses(s.svg, s.over)} width={w} height={h} aria-hidden data-testid="live-paths">
        {boxes
          .filter((b) => lit.has(b.lane.id))
          .map((b) => {
            const ends = [...lit.get(b.lane.id)!].map((id) => b.rows.get(id)).filter((y) => y !== undefined);
            if (!ends.length) return null;
            return (
              <g key={b.lane.id} data-live-lane={b.lane.id}>
                <path d={elbow(hubX, relayBottom, b.spine, LANES_Y, TRUNK_Y)} fill="none" stroke={vt.cyan} strokeWidth={1.8} />
                <line x1={b.spine} x2={b.spine} y1={LANES_Y + HEAD} y2={Math.max(...ends)} stroke={vt.cyan} strokeWidth={1.8} />
                {ends.map((y) => (
                  <line key={y} x1={b.spine} x2={b.spine + STUB} y1={y} y2={y} stroke={vt.cyan} strokeWidth={1.8} />
                ))}
              </g>
            );
          })}
      </svg>
    </div>
  );
}
